import React, { Fragment, useCallback, useEffect, useState, lazy, Suspense } from 'react'
import PropTypes from 'prop-types'

import Spacer from '../../_layout/Spacer'
import { ComponentVariant } from '../../../utils/defaultTypes'

const RegionWindow = lazy(() => import('./RegionWindow'))
const RegionWindowRedirect = lazy(() => import('./RegionWindowRedirect'))
const M000 = lazy(() => import('../../organisms/M.M000/M000'))
const M001 = lazy(() => import('../../organisms/M.M001/M001'))
const M002 = lazy(() => import('../../organisms/M.M002/M002'))
const M004 = lazy(() => import('../../organisms/M.M004/M004'))
const M005 = lazy(() => import('../../organisms/M.M005/M005'))
const M006 = lazy(() => import('../../organisms/M.M006/M006'))
const M007 = lazy(() => import('../../organisms/M.M007/M007'))
const M008 = lazy(() => import('../../organisms/M.M008/M008'))
const M009 = lazy(() => import('../../organisms/M.M009/M009'))
const M011 = lazy(() => import('../../organisms/M.M011/M011'))
const M014 = lazy(() => import('../../organisms/M.M014/M014'))
const M016 = lazy(() => import('../../organisms/M.M016/M016'))
const M019 = lazy(() => import('../../organisms/M.M019/M019'))
const M021 = lazy(() => import('../../organisms/M.M021/M021'))
const M034 = lazy(() => import('../../organisms/M.M034/M034'))
const M035 = lazy(() => import('../../organisms/M.M035/M035'))
const M036 = lazy(() => import('../../organisms/M.M036/M036'))
const M044 = lazy(() => import('../../organisms/M.M044/M044'))
const M047 = lazy(() => import('../../organisms/M.M047/M047'))
const M048 = lazy(() => import('../../organisms/M.M048/M048'))
const M049 = lazy(() => import('../../organisms/M.M049/M049'))
const M050 = lazy(() => import('../../organisms/M.M050/M050'))
const Gluecksrezepte = lazy(() => import('../../organisms/Gluecksrezepte/Gluecksrezepte'))
const CancerPrevention = lazy(() => import('../../organisms/CancerPrevention/CancerPrevention'))
const E001 = lazy(() => import('../../organisms/M.E001/E001'))

const LoadableFallback = ({ elementIndex, onHasLoaded }) => {
  useEffect(() => {
    onHasLoaded(elementIndex, false)
    return () => {
      onHasLoaded(elementIndex, true)
    }
  }, [elementIndex, onHasLoaded])

  return null
}

const ContentSwitch = ({ content, structuredDataInformation, onLoadedComponents, existsInRegionWindow }) => {
  const [loaded, setLoaded] = useState([...Array(content?.length || 0).keys()].map(() => false))

  const handleComponentLoad = useCallback((componentIndex, hasLoaded) => {
    setLoaded((oldLoadedState) => oldLoadedState.map((value, index) => (index === componentIndex ? hasLoaded : value)))
  }, [])

  useEffect(() => {
    if (loaded.every((component) => component === true)) onLoadedComponents()
  }, [loaded, onLoadedComponents])

  return (
    <>
      {content.map((element, index) => (
        <Fragment key={index}>
          {index !== 0 && <Spacer className="h-48 600:h-64 840:h-80" />}

          {(() => {
            return (
              <Suspense fallback={<LoadableFallback onHasLoaded={handleComponentLoad} elementIndex={index} />}>
                {(() => {
                  switch (element.type) {
                    case 'M000':
                      return <M000 key={index} {...element} />

                    case 'M001':
                      return <M001 key={index} {...element} />

                    case 'M002':
                      return <M002 key={index} structuredDataInformation={structuredDataInformation} {...element} />

                    case 'M004':
                      return <M004 key={index} {...element} />

                    case 'M005':
                      return <M005 key={index} {...element} />

                    case 'M006':
                      return <M006 key={index} {...element} />

                    case 'M007':
                      return <M007 key={index} {...element} />

                    case 'M008':
                      return <M008 key={index} {...element} />

                    case 'M009':
                      return <M009 key={index} {...element} />

                    case 'M011':
                      return <M011 key={index} {...element} existsInRegionWindow={!!existsInRegionWindow} />

                    case 'M014':
                      return <M014 key={index} {...element} />

                    case 'M016':
                      return <M016 key={index} {...element} />

                    case 'M019':
                      return <M019 key={index} {...element} />

                    case 'M021':
                      return <M021 key={index} {...element} />

                    case 'M034':
                      return <M034 key={index} {...element} />

                    case 'M035':
                      return <M035 key={index} {...element} />

                    case 'M036':
                      return <M036 key={index} {...element} />

                    case 'M044':
                      return <M044 key={index} {...element} />

                    case 'M047':
                      return <M047 key={index} {...element} />

                    case 'M048':
                      return <M048 key={index} {...element} />

                    case 'M049':
                      return <M049 key={index} {...element} />

                    case 'M050':
                      return <M050 key={index} {...element} />

                    case 'window':
                      return <RegionWindow key={index} {...element} />

                    case 'window_redirect':
                      return <RegionWindowRedirect key={index} {...element} />

                    case 'gluecksrezepte':
                      return <Gluecksrezepte />

                    case 'cancer_prevention':
                      return <CancerPrevention />

                    case 'E001':
                      return <E001 key={index} {...element} settings={{ variant: ComponentVariant.MEDIUM }} />

                    default:
                      return null
                  }
                })()}
              </Suspense>
            )
          })()}
        </Fragment>
      ))}
    </>
  )
}

ContentSwitch.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ),
  structuredDataInformation: PropTypes.shape({
    image: PropTypes.string,
    headline: PropTypes.string,
    datePublished: PropTypes.string,
    dateModified: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
  }),
  onLoadedComponents: PropTypes.func,
}

ContentSwitch.defaultProps = {
  content: [],
  onLoadedComponents: () => {},
}

export default ContentSwitch
